import type { TAutoReplyMessageAsset } from 'api/autoReply';
import type { ScenarioAssetReference, ScenarioMessageV1 } from 'api/scenarios';
import { AssetBubble, ImageBubble, isCardCarouselAsset, mediaActionLabel, TextBubble, VideoBubble } from 'components/MessagePreview';
import { previewNameInsertion } from 'utils/nameInsertion';

export const messageTypeLabels: Record<ScenarioMessageV1['type'], string> = {
  text: 'テキスト',
  image: '画像',
  video: '動画',
  richMessage: 'リッチメッセージ',
  cardTypeMessage: 'カードタイプ',
};

export function assetOptionLabel(assetId: number, reference?: ScenarioAssetReference) {
  if (!reference) return `素材 #${assetId}（読み込み中）`;
  return reference.status === 'deleted' ? `削除済み素材 #${assetId}` : reference.name;
}

function Placeholder({ children }: { children: string }) {
  return <p className="rounded-2xl border border-dashed border-border px-3 py-2 text-[11px] text-muted">{children}</p>;
}

function Chip({ children }: { children: string }) {
  // ellipsisはblock containerにしか効かないのでinline-blockにする
  return <span className="inline-block max-w-full truncate rounded-full border border-border/70 bg-white px-3 py-1 text-[11px] font-semibold text-ink" title={children}>{children}</span>;
}

type BubbleProps = { message: ScenarioMessageV1; assets: TAutoReplyMessageAsset[]; references: ScenarioAssetReference[] };

/** 一斉送信のプレビューと同じ描画を使い、実際に届く見た目だけを出す。 */
function Bubble({ message, assets, references }: BubbleProps) {
  if (message.type === 'text') {
    const content = message.content?.trim();
    if (!content) return <Placeholder>本文が未入力です</Placeholder>;
    return <div className="w-fit min-w-0 max-w-full"><TextBubble text={previewNameInsertion(content)} /></div>;
  }
  if (message.type === 'image') {
    if (!message.media) return <Placeholder>画像が未選択です</Placeholder>;
    const { url, originalWidth, originalHeight } = message.media;
    if (!url) return <Placeholder>画像を表示できません</Placeholder>;
    // 枠自体が縮小表示なので、届くときの幅比率ではなく枠いっぱいに出して中身を確認しやすくする
    return <div className="min-w-0 max-w-full"><ImageBubble url={url} width={originalWidth} height={originalHeight} alt="画像のプレビュー" /></div>;
  }
  if (message.type === 'video') {
    if (!message.media) return <Placeholder>動画が未選択です</Placeholder>;
    const { url, poster, actionUrl, actionLabel } = message.media;
    if (!url) return <Placeholder>動画を表示できません</Placeholder>;
    return <div className="min-w-0 max-w-full"><VideoBubble url={url} poster={poster} actionLabel={mediaActionLabel(actionUrl, actionLabel)} /></div>;
  }
  const assetId = message.assetId;
  if (assetId === null) return <Placeholder>素材が未選択です</Placeholder>;
  const asset = assets.find(({ id }) => id === assetId);
  // 素材一覧の読み込み前や削除済みは中身を描けないので、名前だけで代替する
  if (!asset) return <Chip>{`${messageTypeLabels[message.type]}：${assetOptionLabel(assetId, references.find((reference) => reference.assetId === assetId))}`}</Chip>;
  // 中身が見えていれば素材名のラベルは足さない（届く見た目だけを出す）
  const isCarousel = isCardCarouselAsset(asset);
  return (
    <div
      data-testid={isCarousel ? 'message-preview-carousel' : undefined}
      className={isCarousel ? 'w-full min-w-0' : 'min-w-0 max-w-full'}
    >
      <AssetBubble kind={message.type} asset={asset} />
    </div>
  );
}

type Props = { step: number; messages: ScenarioMessageV1[]; assets: TAutoReplyMessageAsset[]; references: ScenarioAssetReference[] };

/** 送信されるメッセージをトーク画面に近い見た目で並べる、読み取り専用の確認用表示。 */
export function MessagePreview({ step, messages, assets, references }: Props) {
  return (
    // ステップごとに同名のランドマークが並ばないよう、読み上げ名にステップ番号を入れる
    <section aria-label={`ステップ${step} の送信プレビュー`} className="grid min-w-0 content-start gap-2 rounded-2xl border border-border/60 bg-neutral-25 p-3">
      <p aria-hidden className="text-[11px] font-semibold text-muted">送信プレビュー</p>
      {messages.length ? (
        // grid itemは既定のmin-width:autoで縮まないので、各段でmin-w-0を通してカードの横スクロールを効かせる
        <ul data-testid="message-preview-list" className="grid min-w-0 gap-2">
          {messages.map((message) => (
            <li key={message.id} data-testid="message-preview-item" className="min-w-0">
              <Bubble message={message} assets={assets} references={references} />
            </li>
          ))}
        </ul>
      ) : (
        <Placeholder>メッセージがありません</Placeholder>
      )}
    </section>
  );
}
